import { DateTime } from 'luxon';

import { colors } from 'packages/common-frontend/colors';

/**
 * A deal's servicing periods as the portal renders them: one row per scheduled
 * deliverable, joined to its cycle once the tape has reconciled. Shared by the
 * Deliverables table and the period-scoped Overview.
 */

type PeriodInput = { name: string; status: string };

// Optional-and-nullable to match the generated GraphQL types for nullable fields.
type Period = {
  reportingPeriod: string;
  dueDate?: string | null;
  inputs: PeriodInput[];
  cycle?: object | null;
};

/** Today as a FullDateStr, the format periods and due dates compare in. */
export function todayFullDate(): string {
  return DateTime.now().toISODate() ?? '';
}

/** An input's status (awaiting | processing | received | failed) as a label. */
export function inputStatusLabel(status: string): string {
  if (status === 'received') return 'Received';
  if (status === 'failed') return 'Failed';
  if (status === 'processing') return 'Processing';
  return 'Awaiting';
}

/** Colour for an input status, matching {@link inputStatusLabel}. */
export function inputStatusColor(status: string): string {
  if (status === 'received') return colors.green[400];
  if (status === 'failed') return colors.red[400];
  return colors.gray[500];
}

/** Each input and its status, e.g. "Data Tape: Failed · MSR: Awaiting". */
export function formatInputStatuses(inputs: PeriodInput[]): string {
  return inputs
    .map((input) => `${input.name}: ${inputStatusLabel(input.status)}`)
    .join(' · ');
}

/** True when the latest upload of any of the period's inputs failed. */
export function hasFailedInput(period: Period): boolean {
  return period.inputs.some((i) => i.status === 'failed');
}

/** What the period's files link does in the drawer, by where its inputs stand. */
export type PeriodFilesAction = 'upload' | 'view' | 'download';

/**
 * Upload while an input is still owed (awaiting, or its latest upload failed),
 * view while one is processing, download once every input is received.
 */
export function periodFilesAction(period: Period): PeriodFilesAction {
  if (
    period.inputs.length === 0 ||
    period.inputs.some((i) => i.status === 'awaiting' || i.status === 'failed')
  ) {
    return 'upload';
  }
  if (period.inputs.some((i) => i.status !== 'received')) {
    return 'view';
  }
  return 'download';
}

export const periodFilesActionLabel: { [K in PeriodFilesAction]: string } = {
  upload: 'Upload files',
  view: 'View progress',
  download: 'Download files',
};

export function isOverdue(period: Period, today: string): boolean {
  return period.dueDate ? period.dueDate < today : false;
}

/**
 * Where a period with no cycle stands: what it waits on, flagged overdue once its
 * due date has passed (e.g. "Overdue — awaiting tape").
 */
export function awaitingPeriodLabel(period: Period, today: string): string {
  const getStage = (): string => {
    if (hasFailedInput(period)) {
      return 'input failed';
    }
    if (period.inputs.some((i) => i.status !== 'awaiting')) {
      return 'processing';
    }
    return 'awaiting tape';
  };
  const stage = getStage();
  return isOverdue(period, today)
    ? `Overdue — ${stage}`
    : stage.charAt(0).toUpperCase() + stage.slice(1);
}

/** Colour for {@link awaitingPeriodLabel}: red once overdue or failed. */
export function awaitingPeriodColor(period: Period, today: string): string {
  return isOverdue(period, today) || hasFailedInput(period)
    ? colors.red[400]
    : colors.gray[500];
}
